import { ArrowRight, CheckCircle2 } from "lucide-react";

import type { MissionHandoff } from "@/entities/missions";

type Props = {
  headline: string;
  detail?: string;
  handoff: MissionHandoff;
  /**
   * The framework never *creates* the next mission; the parent decides
   * how to accept the handoff (start a real mission, navigate, etc.).
   * Pass ``undefined`` to render only the informational panel.
   */
  onAcceptHandoff?: (nextMissionType: string) => void;
  /** Optional label override — defaults to a friendly imperative. */
  acceptLabel?: string;
  testIdRoot?: string;
};

/**
 * Completion handoff card.
 *
 * Rendered after a mission's ``COMPLETED`` state. Communicates the
 * outcome and offers exactly one next action (never a dead end, per
 * the Focus Flow doctrine).
 */
export function MissionCompleteHandoff({
  headline,
  detail,
  handoff,
  onAcceptHandoff,
  acceptLabel,
  testIdRoot = "mission-complete",
}: Props) {
  const next = handoff.recommended_next;
  return (
    <div
      className="mx-auto max-w-lg rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-6 md:p-7"
      data-testid={testIdRoot}
      data-handoff-next={next ?? ""}
      data-handoff-type={handoff.handoff_type ?? "recommend_next"}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-3">
        <CheckCircle2 className="h-6 w-6 text-emerald-400" aria-hidden />
        <h2 className="text-lg font-semibold text-white" data-testid={`${testIdRoot}-headline`}>
          {headline}
        </h2>
      </div>
      {detail && (
        <p className="mt-2 text-sm text-white/70" data-testid={`${testIdRoot}-detail`}>
          {detail}
        </p>
      )}
      {next && onAcceptHandoff && (
        <button
          type="button"
          onClick={() => onAcceptHandoff(next)}
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 via-sky-500 to-violet-500 px-4 py-2 text-sm font-semibold text-slate-950 shadow-[0_10px_30px_-10px_rgba(0,240,255,0.5)] hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition"
          data-testid={`${testIdRoot}-next-cta`}
        >
          {acceptLabel || "Continue"}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </button>
      )}
    </div>
  );
}
